import { POSITION, useToast } from 'vue-toastification'

import { useAppContentStore } from '@/stores/appContent'

import { getTextContentItemValueFromContentModule, substitute } from '@/composables/contentful'
import type {
  RewardAddedCrossing,
  RewardRemovedCrossing
} from '@/composables/useRewardThresholdCrossing'
import { formatCurrency } from '@/lib/utils'

import RewardToast from '@/components/Shared/RewardsTracker/RewardToast.vue'

import type { ContentfulContentModule } from '#contentful/ContentfulContentModule'

// Toast copy in the `rewards-tracker` content module, with in-code fallbacks so the toasts still
// fire (and stay grep-able) if the module is absent. Generic 🎁 on "Added" (not the tier-specific
// 🚚) since the next reward varies — consistent with the footer call-out.
const TOAST = {
  added: {
    id: 'rewards-tracker-toast-added',
    fallback: "🎁 Added! You're only **##AmountToNext##** away from **##NextRewardName##**!"
  },
  allUnlocked: {
    id: 'rewards-tracker-toast-all-unlocked',
    fallback: "🎊 Incredible — you've unlocked every reward!"
  },
  removed: {
    id: 'rewards-tracker-toast-removed',
    fallback:
      '⚠️ **##RemovedRewardName##** removed. Add **##ReclaimAmount##** to your cart to reclaim!'
  }
} as const

function read(
  module: ContentfulContentModule | null,
  item: { id: string; fallback: string }
): string {
  return getTextContentItemValueFromContentModule(module, item.id, item.fallback) ?? item.fallback
}

/** Resolves the Added-toast copy; falls back to the all-unlocked message when the last tier crossed. */
export function buildAddedToast(
  crossing: RewardAddedCrossing,
  module: ContentfulContentModule | null
): string {
  if (crossing.allUnlocked || !crossing.nextReward) {
    return read(module, TOAST.allUnlocked)
  }
  return substitute(read(module, TOAST.added), {
    AmountToNext: formatCurrency(crossing.nextReward.amountToUnlock),
    NextRewardName: crossing.nextReward.label
  })
}

/** Resolves the Removed-toast copy (removed reward name + reclaim amount). */
export function buildRemovedToast(
  crossing: RewardRemovedCrossing,
  module: ContentfulContentModule | null
): string {
  return substitute(read(module, TOAST.removed), {
    RemovedRewardName: crossing.removedReward.label,
    ReclaimAmount: formatCurrency(crossing.reclaimAmount)
  })
}

export interface UseRewardToasts {
  showAddedToast: (crossing: RewardAddedCrossing) => void
  showRemovedToast: (crossing: RewardRemovedCrossing) => void
}

/**
 * Fires the threshold-cross toasts (top-center, auto-dismiss) via vue-toastification, rendering the
 * resolved copy in `RewardToast`. Shared by the live wiring (`App.vue` + `useRewardThresholdCrossing`)
 * and the `/debug/rewards-tracker` harness triggers.
 */
export function useRewardToasts(): UseRewardToasts {
  const toast = useToast()
  const appContentStore = useAppContentStore()

  const fire = (body: string): void => {
    // Clear any toasts still on screen (e.g. a generic cart toast lingering from an earlier
    // non-crossing add) before showing the reward toast, so the two never overlap — on mobile both
    // span near full-width (BugHerd #108). The threshold watcher fires at most one reward toast per
    // mutation, so this never wipes a sibling reward toast from the same crossing.
    toast.clear()
    toast(
      { component: RewardToast, props: { body } },
      {
        toastClassName: 'reward-toast',
        icon: false,
        closeButton: false,
        position: POSITION.TOP_CENTER,
        timeout: 5000
      }
    )
  }

  return {
    showAddedToast: (crossing) =>
      fire(buildAddedToast(crossing, appContentStore.rewardsTrackerContent)),
    showRemovedToast: (crossing) =>
      fire(buildRemovedToast(crossing, appContentStore.rewardsTrackerContent))
  }
}
